import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LEAD_TEMPERATURES, PRIORITIES } from '@stoplord/shared';
import { leadsApi } from '@/services/crm';
import type { Lead } from '@/services/types';
import { EMPTY, useAuth } from '@/store/auth';
import { toast } from '@/store/ui';
import { useCan, useTerms } from '@/hooks/usePermission';
import { ApiError } from '@/lib/api';
import { humanize, toLocalInput } from '@/lib/format';
import { Button, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { UserSelect } from '../shared/UserPicker';
import { compact, handleFormError } from '../shared/forms';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a contact name').max(120),
  company: z.string().max(120).optional(),
  email: z.union([z.literal(''), z.string().email('Invalid email')]).optional(),
  phone: z.union([z.literal(''), z.string().regex(/^[+()\d\s-]{6,20}$/, 'Invalid phone')]).optional(),
  source: z.string().min(1),
  temperature: z.enum(LEAD_TEMPERATURES),
  priority: z.enum(PRIORITIES),
  estimatedValue: z.string().optional(),
  requirement: z.string().max(2000).optional(),
  ownerId: z.string().optional(),
  nextFollowUpAt: z.string().optional(),
  tags: z.string().optional(),
});
type Form = z.infer<typeof schema>;

const DRAFT_KEY = 'stoplord-draft-lead';

export function LeadFormModal({ open, onClose, lead }: { open: boolean; onClose: () => void; lead?: Lead }) {
  const terms = useTerms();
  const can = useCan();
  const sources = useAuth((s) => s.tenant?.leadSources ?? EMPTY);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editing = !!lead;
  const { register, handleSubmit, formState, setError, reset, control, watch } = useForm<Form>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) return;
    let draft: Partial<Form> = {};
    if (!editing) {
      try {
        draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? '{}');
      } catch {
        /* ignore */
      }
    }
    reset({
      name: lead?.name ?? '',
      company: lead?.company ?? '',
      email: lead?.email ?? '',
      phone: lead?.phone ?? '',
      source: lead?.source ?? sources[0]?.key ?? 'other',
      temperature: lead?.temperature ?? 'warm',
      priority: lead?.priority ?? 'medium',
      estimatedValue: lead?.estimatedValue ? String(lead.estimatedValue) : '',
      requirement: lead?.requirement ?? '',
      ownerId: '',
      nextFollowUpAt: '',
      tags: lead?.tags.join(', ') ?? '',
      ...draft,
    });
  }, [open, lead, editing, reset, sources]);

  // Keep an unsaved draft for new leads so an idle sign-out does not lose typing.
  useEffect(() => {
    if (editing || !open) return;
    const sub = watch((v) => {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(v));
      } catch {
        /* ignore */
      }
    });
    return () => sub.unsubscribe();
  }, [watch, editing, open]);

  const mut = useMutation({
    mutationFn: async (v: Form & { allowDuplicate?: boolean }) => {
      const body = compact({
        name: v.name,
        company: v.company,
        email: v.email,
        phone: v.phone,
        source: v.source,
        temperature: v.temperature,
        priority: v.priority,
        estimatedValue: v.estimatedValue ? Number(v.estimatedValue) : undefined,
        requirement: v.requirement,
        tags: (v.tags ?? '').split(',').map((t) => t.trim()).filter(Boolean),
      });
      if (editing) return leadsApi.update(lead!._id, body);
      return leadsApi.create({ ...body, ownerId: v.ownerId || undefined, nextFollowUpAt: v.nextFollowUpAt ? new Date(v.nextFollowUpAt).toISOString() : undefined, allowDuplicate: v.allowDuplicate });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (!editing) sessionStorage.removeItem(DRAFT_KEY);
      toast.success(editing ? `${terms.lead.singular} updated` : `${terms.lead.singular} created`, saved.leadNumber);
      onClose();
      if (!editing) navigate(`/crm/leads/${saved._id}`);
    },
  });

  const submit = (allowDuplicate = false) =>
    handleSubmit(async (v) => {
      try {
        await mut.mutateAsync({ ...v, allowDuplicate });
      } catch (e) {
        if (e instanceof ApiError && e.code === 'DUPLICATE') {
          const dups = (e.details as { duplicates: { leadNumber: string; name?: string; visible: boolean }[] })?.duplicates ?? [];
          setError('root', { message: `Possible duplicate: ${dups.map((d) => (d.visible ? `${d.leadNumber} ${d.name}` : `${d.leadNumber} (owned by another team)`)).join(', ')}` });
          return;
        }
        handleFormError(e, setError);
      }
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${lead!.leadNumber}` : `New ${terms.lead.singular.toLowerCase()}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          {formState.errors.root && !editing && <Button variant="soft" onClick={submit(true)} loading={mut.isPending}>Create anyway</Button>}
          <Button onClick={submit(false)} loading={mut.isPending}>{editing ? 'Save changes' : 'Create'}</Button>
        </>
      }
    >
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit(false)} noValidate>
        {formState.errors.root && <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning sm:col-span-2" role="alert">{formState.errors.root.message}</p>}
        <Field label="Contact name" required error={formState.errors.name?.message}>{(p) => <Input {...p} {...register('name')} />}</Field>
        <Field label="Company" error={formState.errors.company?.message}>{(p) => <Input {...p} {...register('company')} />}</Field>
        <Field label="Email" error={formState.errors.email?.message}>{(p) => <Input {...p} type="email" {...register('email')} />}</Field>
        <Field label="Phone" error={formState.errors.phone?.message}>{(p) => <Input {...p} type="tel" {...register('phone')} />}</Field>
        <Field label="Source">{(p) => <Select {...p} {...register('source')}>{sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</Select>}</Field>
        <Field label="Estimated value" error={formState.errors.estimatedValue?.message}>{(p) => <Input {...p} type="number" min={0} step="1" {...register('estimatedValue')} />}</Field>
        <Field label="Temperature">{(p) => <Select {...p} {...register('temperature')}>{LEAD_TEMPERATURES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        <Field label="Priority">{(p) => <Select {...p} {...register('priority')}>{PRIORITIES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        {!editing && can('crm.leads.assign') && (
          <Field label="Assign to" hint="Leave empty to assign to yourself (or round-robin if enabled).">
            {(p) => <Controller control={control} name="ownerId" render={({ field }) => <UserSelect id={p.id} value={field.value ?? ''} onChange={field.onChange} placeholder="Me / automatic" />} />}
          </Field>
        )}
        {!editing && <Field label="First follow-up">{(p) => <Input {...p} type="datetime-local" min={toLocalInput(new Date())} {...register('nextFollowUpAt')} />}</Field>}
        <Field label="Requirement" className="sm:col-span-2">{(p) => <Textarea {...p} rows={3} {...register('requirement')} />}</Field>
        <Field label="Tags" hint="Comma separated" className="sm:col-span-2">{(p) => <Input {...p} {...register('tags')} />}</Field>
      </form>
    </Modal>
  );
}
